import { indices, type Settings, wordsOf } from './featuriser'

const MAGIC = 'JTCM'
const FORMAT_VERSION = 1
const VERSION_OFFSET = MAGIC.length
const HEADER_LENGTH_OFFSET = VERSION_OFFSET + Uint16Array.BYTES_PER_ELEMENT
const PREAMBLE_LENGTH = HEADER_LENGTH_OFFSET + Uint32Array.BYTES_PER_ELEMENT
const FLOAT32_SIZE = Float32Array.BYTES_PER_ELEMENT

export interface Header {
	name: string
	language: string
	labels: string[]
	titles: Record<string, string>
	thresholds: Record<string, number>
	connectors: string[]
	/** Label => fewest words a text needs before the label can be detected; 1 when absent. */
	minWords: Record<string, number>
	settings: Settings
}

export interface Classifier extends Header {
	score(text: string): Record<string, number>
	wordCount(text: string): number
}

/**
 * Reads the model file written by TextClassifierModel::toBinary() in Nexus.
 */
export function parseModel(buffer: ArrayBuffer): Classifier {
	const view = new DataView(buffer)
	const magic = new TextDecoder().decode(new Uint8Array(buffer, 0, MAGIC.length))

	if (buffer.byteLength < PREAMBLE_LENGTH || magic !== MAGIC) {
		throw new Error('Not a text classifier model file.')
	}

	const version = view.getUint16(VERSION_OFFSET, true)
	if (version !== FORMAT_VERSION) {
		throw new Error(`Unsupported text classifier model format version ${version}.`)
	}

	const headerLength = view.getUint32(HEADER_LENGTH_OFFSET, true)
	const header: Header = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, PREAMBLE_LENGTH, headerLength)))
	const labelCount = header.labels.length

	let offset = PREAMBLE_LENGTH + headerLength
	const scales = readFloats(view, offset, labelCount)
	offset += labelCount * FLOAT32_SIZE
	const biases = readFloats(view, offset, labelCount)
	offset += labelCount * FLOAT32_SIZE
	const weights = new Int8Array(buffer, offset, header.settings.hashSize * labelCount)

	return {
		...header,
		score(text: string): Record<string, number> {
			const logits = [...biases]

			for (const index of indices(text, header.settings, header.connectors)) {
				for (let label = 0; label < labelCount; label++) {
					logits[label] += weights[index * labelCount + label] * scales[label]
				}
			}

			return Object.fromEntries(
				header.labels.map((label, position) => [label, 1 / (1 + Math.exp(-logits[position]))]),
			)
		},
		wordCount(text: string): number {
			return wordsOf(text, header.settings, header.connectors).length
		},
	}
}

function readFloats(view: DataView, offset: number, count: number): number[] {
	return Array.from({ length: count }, (_, position) => view.getFloat32(offset + position * FLOAT32_SIZE, true))
}
